import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Megaphone, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useAds, useBoostAction, type AdBoost } from "@/hooks/use-ads";
import {
  useAutomationConfig,
  useUpdateAutomationConfig,
} from "@/hooks/use-automation-config";
import { useLocale, formatNumber, formatDate } from "@/i18n/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { BoostStatusBadge } from "@/components/ads/boost-status-badge";
import { ActivateBoostDialog } from "@/components/ads/activate-boost-dialog";
import { BoostDetailSheet } from "@/components/ads/boost-detail-sheet";

function num(v: string | number | null | undefined): number {
  const n = typeof v === "string" ? Number(v) : (v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function SummaryTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function BrandAdsPage() {
  const { t } = useTranslation("ads");
  const { brand } = useBrandContext();
  const { canViewAnalytics, canManageBrand } = usePermissions();
  const navigate = useNavigate();
  const { boostId } = useParams<{ boostId: string }>();
  const brandId = brand?.id ?? "";
  const prefix = `/${brand?.slug ?? ""}`;
  const locale = useLocale();

  const ads = useAds(brandId);
  const config = useAutomationConfig(brandId);
  const updateConfig = useUpdateAutomationConfig(brandId);
  const boostAction = useBoostAction(brandId);
  const [activating, setActivating] = useState<AdBoost | null>(null);

  function usdFmt(v: string | number | null | undefined): string {
    return formatNumber(num(v), locale, { style: "currency", currency: "USD" });
  }

  const PAUSE_REASON_KEYS = ["manual", "budget", "provider_errors"] as const;
  type PauseReasonKey = (typeof PAUSE_REASON_KEYS)[number];
  function getPauseReason(reason: string | null | undefined): string {
    if (reason && (PAUSE_REASON_KEYS as readonly string[]).includes(reason)) {
      return t(`pauseBanner.reason.${reason as PauseReasonKey}`);
    }
    return t("pauseBanner.reason.default");
  }

  if (!canViewAnalytics) {
    return <p className="p-6 text-muted-foreground">{t("page.noAccess")}</p>;
  }
  if (ads.isLoading) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-72 w-full rounded-lg" />
      </div>
    );
  }
  if (ads.isError) {
    return <p className="p-6 text-destructive">{t("page.loadError")}</p>;
  }

  const data = ads.data!;
  const { account, boosts, summary } = data;
  const cfg = config.data;

  function runAction(boost: AdBoost, action: "pause" | "resume" | "cancel", confirmKey: string) {
    if (!window.confirm(t(confirmKey as Parameters<typeof t>[0]))) return;
    boostAction.mutate({ boostId: boost.id, action });
  }

  async function resumeAds() {
    if (!window.confirm(t("confirms.resumeAds"))) {
      return;
    }
    // Standalone single-flag mutation — never bundled with any form state.
    await updateConfig.mutateAsync({ resumeAds: true });
    toast.success(t("toasts.adsResumed"));
  }

  function rowActions(boost: AdBoost) {
    if (!canManageBrand) return null;
    const pending = boostAction.isPending;
    switch (boost.status) {
      case "SUGGESTED":
        return (
          <>
            <Button size="sm" disabled={pending} onClick={() => setActivating(boost)}>
              {t("actions.activate")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => runAction(boost, "cancel", "confirms.dismissBoost")}
            >
              {t("actions.dismiss")}
            </Button>
          </>
        );
      case "ACTIVE":
      case "PENDING":
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => runAction(boost, "pause", "confirms.pauseBoost")}
          >
            {t("actions.pause")}
          </Button>
        );
      case "PAUSED":
        return (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => runAction(boost, "resume", "confirms.resumeBoost")}
            >
              {t("actions.resume")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => runAction(boost, "cancel", "confirms.cancelBoost")}
            >
              {t("actions.cancel")}
            </Button>
          </>
        );
      case "FAILED":
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => runAction(boost, "cancel", "confirms.cancelFailed")}
          >
            {t("actions.cancel")}
          </Button>
        );
      default:
        return null;
    }
  }

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("page.subtitle")}
        </p>
      </header>

      {cfg?.adsPausedAt && (
        <div className="flex items-start justify-between gap-3 rounded-md bg-amber-500/10 p-3 text-sm text-amber-400">
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>
              {getPauseReason(cfg.adsPauseReason)}{" "}
              <span className="text-amber-400/70">
                ({formatDate(cfg.adsPausedAt, locale, { dateStyle: "medium", timeStyle: "short" })})
              </span>
            </p>
          </div>
          {canManageBrand && (
            <Button
              size="sm"
              variant="outline"
              disabled={updateConfig.isPending}
              onClick={() => void resumeAds()}
            >
              {t("pauseBanner.resumeButton")}
            </Button>
          )}
        </div>
      )}

      {cfg && !cfg.adsEnabled && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
          {t("adsDisabledBanner")}{" "}
          <Link to={`${prefix}/settings/automation`} className="underline hover:text-foreground">
            {t("adsDisabledLink")}
          </Link>
          .
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryTile
          label={t("summary.committedThisMonth")}
          value={usdFmt(summary.committedThisMonthUsd)}
          sub={
            summary.capUsd != null
              ? t("summary.capSub", { cap: usdFmt(summary.capUsd) })
              : t("summary.noCap")
          }
        />
        <SummaryTile label={t("summary.activeBoosts")} value={String(summary.activeCount)} />
        <SummaryTile
          label={t("summary.adAccount")}
          value={account ? (account.name ?? account.platformAdAccountId) : t("summary.notConfigured")}
          sub={account ? `${account.platformAdAccountId} · ${account.currency}` : undefined}
        />
      </div>

      {!account ? (
        <EmptyState
          icon={Megaphone}
          title={t("emptyState.noAccount.title")}
          description={
            canManageBrand
              ? t("emptyState.noAccount.descAdmin")
              : t("emptyState.noAccount.descViewer")
          }
          action={
            canManageBrand
              ? {
                  label: t("emptyState.noAccount.actionLabel"),
                  onClick: () => navigate(`${prefix}/settings/automation`),
                }
              : undefined
          }
        />
      ) : boosts.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title={t("emptyState.noBoosts.title")}
          description={t("emptyState.noBoosts.description")}
        />
      ) : (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">{t("table.col.piece")}</th>
                <th className="px-4 py-2 font-medium">{t("table.col.status")}</th>
                <th className="px-4 py-2 font-medium">{t("table.col.budget")}</th>
                <th className="px-4 py-2 font-medium">{t("table.col.spend")}</th>
                <th className="px-4 py-2 font-medium">{t("table.col.imprClicksCpm")}</th>
                <th className="px-4 py-2 font-medium">{t("table.col.created")}</th>
                {canManageBrand && <th className="px-4 py-2 font-medium">{t("table.col.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {boosts.map((boost) => (
                <tr
                  key={boost.id}
                  className="cursor-pointer border-b border-border/50 hover:bg-accent/50"
                  onClick={() => navigate(`${prefix}/ads/${boost.id}`)}
                >
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {boost.piece?.thumbnailUrl ? (
                        <img
                          src={boost.piece.thumbnailUrl}
                          alt=""
                          className="h-8 w-8 flex-shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 flex-shrink-0 rounded bg-muted" />
                      )}
                      <div className="min-w-0 max-w-xs">
                        <div className="flex items-center gap-1.5">
                          {boost.piece && (
                            <PlatformIcon platform={boost.piece.platform} size={13} />
                          )}
                          <span className="text-xs font-medium">
                            {boost.piece?.shortCode ?? boost.pieceId}
                          </span>
                        </div>
                        {boost.piece?.caption && (
                          <p className="truncate text-xs text-muted-foreground">
                            {boost.piece.caption}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <BoostStatusBadge status={boost.status} />
                  </td>
                  <td className="px-4 py-2">{boost.budgetUsd !== null ? usdFmt(boost.budgetUsd) : "—"}</td>
                  <td className="px-4 py-2">{usdFmt(boost.spendToDateUsd)}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {formatNumber(num(boost.impressions), locale)} / {formatNumber(num(boost.clicks), locale)} /{" "}
                    {boost.cpm !== null ? usdFmt(boost.cpm) : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {formatDate(boost.createdAt, locale)}
                  </td>
                  {canManageBrand && (
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1.5">{rowActions(boost)}</div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ActivateBoostDialog
        boost={activating}
        summary={summary}
        durationDays={cfg?.adsBoostDurationDays ?? null}
        open={activating !== null}
        onOpenChange={(open) => !open && setActivating(null)}
        pending={boostAction.isPending}
        onConfirm={() => {
          if (!activating) return;
          if (boostAction.isPending) return;
          boostAction.mutate(
            { boostId: activating.id, action: "activate" },
            { onSettled: () => setActivating(null) },
          );
        }}
      />

      {/* Deep-linkable: sheet open state derives from the /ads/:boostId param. */}
      <BoostDetailSheet
        brandId={brandId}
        boostId={boostId ?? null}
        onClose={() => navigate(`${prefix}/ads`)}
      />
    </div>
  );
}
